import { useState } from 'react';
import styles from './Stage.module.scss';
import { AxiosError } from 'axios';
import cx from 'classnames';
import { Alert, Button, Icon, Text } from 'components';
import { appActions } from 'store/app/slice';
import { selectCurrentUser } from 'store/auth/selectors';
import { patientsActions } from 'store/patients/slice';
import { useDispatch, useSelector } from 'store/utils';
import { TreatmentPlanTask, UserRole } from 'types';

export type StageInfo = {
  title: string;
  description: string;
  task: TreatmentPlanTask;
  alertText?: string;
  controls: {
    nextStepTask?: TreatmentPlanTask;
    nextStepText?: string;
    stepBackTask?: TreatmentPlanTask;
    stepBackText?: string;
    nextStepDoctorActions?: boolean;
    stepBackDoctorActions?: boolean;
    diagnosisIsRequired?: boolean;
    treatmentPlanIsRequired?: boolean;
    letterLinkRequired?: boolean;
    complaintWithImageIsRequired?: boolean;
  };
};

type Props = {
  isCurrent: boolean;
  isPassed: boolean;
  stageInfo: StageInfo;
  patientCaseId: number;
  isOutOfTreatment: boolean;
  diagnosis: string;
  hasCorrectTreatmentPlan: boolean;
  assignedDoctorId?: string;
  letterLinks: string[];
  hasComplaintWithImage: boolean;
};

const getIcon = ({
  isCurrent,
  isPassed,
  isOutOfTreatment,
}: Pick<Props, 'isCurrent' | 'isPassed' | 'isOutOfTreatment'>) => {
  if (isCurrent && isOutOfTreatment) return <Icon className={styles.icon} name="OutOfTreatment" />;
  if (isCurrent) return <Icon className={styles.icon} name="Progress" />;
  if (isPassed) return <Icon className={styles.icon} name="FinishedSmall" />;
  return <Icon className={styles.icon} name="EmptyCircle" />;
};

export const Stage = ({
  isCurrent,
  isPassed,
  patientCaseId,
  isOutOfTreatment,
  assignedDoctorId,
  diagnosis,
  letterLinks,
  hasCorrectTreatmentPlan,
  hasComplaintWithImage,
  stageInfo: {
    description,
    title,
    alertText,
    controls: {
      nextStepText,
      nextStepTask,
      stepBackText,
      stepBackTask,
      nextStepDoctorActions,
      stepBackDoctorActions,
      diagnosisIsRequired,
      treatmentPlanIsRequired,
      letterLinkRequired,
      complaintWithImageIsRequired,
    },
  },
}: Props) => {
  const dispatch = useDispatch();
  const [errorText, setErrorText] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const currentUser = useSelector(selectCurrentUser);
  if (!currentUser) return null;

  const { role, id } = currentUser;
  const isAssignedDoctor = id === assignedDoctorId;

  const showPrevStep =
    (role === UserRole.CLINIC && stepBackTask) ||
    (role === UserRole.DOCTOR && isAssignedDoctor && stepBackTask && stepBackDoctorActions);
  const showNextStep =
    (role === UserRole.CLINIC && nextStepTask) ||
    (role === UserRole.DOCTOR && isAssignedDoctor && nextStepTask && nextStepDoctorActions);
  const showButtons = showPrevStep || showNextStep;

  const disableNextStepBtn =
    (diagnosisIsRequired && !diagnosis) ||
    (letterLinkRequired && !letterLinks.length) ||
    (treatmentPlanIsRequired && !hasCorrectTreatmentPlan) ||
    (complaintWithImageIsRequired && !hasComplaintWithImage) ||
    (role === UserRole.CLINIC && nextStepDoctorActions);

  const nextStepHandler = async () => {
    if (!nextStepTask || isLoading) return;
    setErrorText('');

    if (nextStepTask === TreatmentPlanTask.IN_TREATMENT) {
      dispatch(appActions.setIsModalOpened({ name: 'startTreatment', isOpened: true }));
      return;
    }
    try {
      setIsLoading(true);
      await dispatch(
        patientsActions.updatePatientCase({
          patientCaseData: {
            task: nextStepTask,
          },
          patientCaseId: patientCaseId,
        }),
      ).unwrap();
    } catch (error) {
      if (error instanceof AxiosError) {
        error.response?.data?.status === 409 && setErrorText(error.response?.data?.title ?? '');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const stepBackHandler = async () => {
    if (!stepBackTask || isLoading) return;

    try {
      setIsLoading(true);
      await dispatch(
        patientsActions.updatePatientCase({
          patientCaseData: {
            task: stepBackTask,
          },
          patientCaseId: patientCaseId,
        }),
      ).unwrap();
    } catch (error) {
      if (error instanceof AxiosError) {
        error.response?.data?.status === 409 && setErrorText(error.response?.data?.title ?? '');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className={cx(styles.container, {
        [styles.isCurrent]: isCurrent,
        [styles.isPassed]: isPassed,
        [styles.isDisabled]: !isPassed && !isCurrent,
        [styles.isOutOfTreatment]: isOutOfTreatment,
      })}>
      <div className={styles.header}>
        <div className={styles.iconContainer}>{getIcon({ isCurrent, isPassed, isOutOfTreatment })}</div>
        <Text type="body1" className={styles.title}>
          {title}
        </Text>
      </div>
      {isCurrent && !isOutOfTreatment && (
        <div className={styles.descriptionAndControls}>
          <div className={styles.description}>
            <Text type="body2">{description}</Text>
            {alertText && (
              <Alert type="warning" size="small">
                {alertText}
              </Alert>
            )}
          </div>
          {showButtons && (
            <div className={styles.buttons}>
              {showNextStep &&
                (disableNextStepBtn ? (
                  <Button onClick={nextStepHandler} type="secondary" className={cx(styles.button, styles.isDisabled)}>
                    {nextStepText}
                  </Button>
                ) : (
                  <Button onClick={nextStepHandler} className={styles.button}>
                    {nextStepText}
                  </Button>
                ))}
              {showPrevStep && (
                <Button onClick={stepBackHandler} type="secondary" className={styles.button}>
                  {stepBackText}
                </Button>
              )}
            </div>
          )}
          {errorText && (
            <Text type="caption" className={styles.errorText}>
              {errorText}
            </Text>
          )}
        </div>
      )}
    </div>
  );
};
